import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Container,
    Paper,
    Grid,
    Chip,
    Alert,
    Tab,
    Tabs,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    IconButton,
    Tooltip,
    Fab,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Divider,
    Card,
    CardContent,
    Stack,
    AppBar,
    Toolbar
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    ContentCopy as ContentCopyIcon,
    ArrowBack as ArrowBackIcon,
    Code as CodeIcon,
    Security as SecurityIcon,
    Speed as SpeedIcon,
    Description as DescriptionIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiDocumentationService, type ApiDocField } from '../services/apiDocumentationService';

// Use ApiDocField from the service instead of local interface
type ApiField = ApiDocField;

interface ApiEndpoint {
    method: 'GET' | 'POST' | 'PUT' | 'DELETE';
    path: string;
    summary: string;
    description: string;
    parameters?: ApiField[];
    requestBody?: ApiField[];
    responses: {
        [status: string]: {
            description: string;
            example?: any;
        };
    };
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel = ({ children, value, index, ...other }: TabPanelProps) => (
    <div
        role="tabpanel"
        hidden={value !== index}
        id={`api-docs-tabpanel-${index}`}
        aria-labelledby={`api-docs-tab-${index}`}
        {...other}
    >
        {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
);

export const ApiDocumentationPage: React.FC = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(0);
    const [apiFields, setApiFields] = useState<ApiField[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isFromAdminConsole, setIsFromAdminConsole] = useState(false);

    useEffect(() => {
        loadApiDocumentation();
    }, []);

    const loadApiDocumentation = async () => {
        try {
            // Load field definitions from the server via API
            const fields = await apiDocumentationService.fetchFieldDefinitions();
            setApiFields(fields);
            
            // Check if data is live or fallback
            const isLive = await apiDocumentationService.isLiveData();
            setIsFromAdminConsole(isLive);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load API documentation');
        } finally {
            setLoading(false);
        }
    };


    const apiEndpoints: ApiEndpoint[] = [
        {
            method: 'POST',
            path: '/api/v1/submit-quote',
            summary: 'Submit a new sail quote request',
            description: 'Submit a complete sail specification for pricing. Returns a quote ID for tracking.',
            requestBody: apiFields,
            responses: {
                '200': {
                    description: 'Quote submitted successfully',
                    example: {
                        success: true,
                        data: {
                            ProductNumber: 'MAIN-2024-BENETEAU-473-001',
                            RequestId: 'REQ-20241225-14:30:25-001',
                            status: 'Received'
                        }
                    }
                },
                '400': {
                    description: 'Invalid request data',
                    example: {
                        success: false,
                        error: 'ProductNumber is required'
                    }
                },
                '429': {
                    description: 'Rate limit exceeded (50 requests per second per user)'
                }
            }
        },
        {
            method: 'GET',
            path: '/api/v1/retrieve-price/:productNumber',
            summary: 'Retrieve pricing for a submitted quote',
            description: 'Get current pricing for a sail quote. Returns different price levels based on user privileges.',
            parameters: [
                {
                    name: 'Product Number',
                    apiFieldName: 'productNumber',
                    type: 'string',
                    required: true,
                    description: 'The product number from the original quote submission'
                }
            ],
            responses: {
                '200': {
                    description: 'Pricing retrieved successfully',
                    example: {
                        success: true,
                        data: {
                            ProductNumber: 'MAIN-2024-BENETEAU-473-001',
                            MSRP: 3250.00,
                            DealerPrice: 2450.00,
                            currency: 'USD'
                        }
                    }
                },
                '404': {
                    description: 'Product not found'
                }
            }
        },
        {
            method: 'POST',
            path: '/api/v1/confirm-order/:productNumber',
            summary: 'Confirm an order from quote to accepted',
            description: 'Move a quote from "Quoted" status to "Accepted" status. This commits the order for production.',
            parameters: [
                {
                    name: 'Product Number',
                    apiFieldName: 'productNumber',
                    type: 'string',
                    required: true,
                    description: 'The product number to confirm'
                }
            ],
            responses: {
                '200': {
                    description: 'Order confirmed successfully',
                    example: {
                        success: true,
                        data: {
                            ProductNumber: 'MAIN-2024-BENETEAU-473-001',
                            status: 'Accepted',
                            confirmedAt: '2024-12-25T14:30:25Z'
                        }
                    }
                },
                '400': {
                    description: 'Invalid status transition (can only move from Quoted to Accepted)'
                }
            }
        },
        {
            method: 'GET',
            path: '/api/v1/check-status/:productNumber',
            summary: 'Check the current status of an order',
            description: 'Get the current production status of a sail order.',
            parameters: [
                {
                    name: 'Product Number',
                    apiFieldName: 'productNumber',
                    type: 'string',
                    required: true,
                    description: 'The product number to check'
                }
            ],
            responses: {
                '200': {
                    description: 'Status retrieved successfully',
                    example: {
                        success: true,
                        data: {
                            ProductNumber: 'MAIN-2024-BENETEAU-473-001',
                            status: 'In Production - Layout',
                            lastUpdated: '2024-12-25T14:30:25Z'
                        }
                    }
                }
            }
        }
    ];

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
    };

    const refreshDocumentation = async () => {
        setLoading(true);
        try {
            // Reload field definitions from server
            const fields = await apiDocumentationService.fetchFieldDefinitions();
            setApiFields(fields);
            
            // Check if data is live or fallback
            const isLive = await apiDocumentationService.isLiveData();
            setIsFromAdminConsole(isLive);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to refresh documentation');
        } finally {
            setLoading(false);
        }
    };

    const getMethodColor = (method: string) => {
        switch (method.toUpperCase()) {
            case 'GET': return 'success';
            case 'POST': return 'primary';
            case 'PUT': return 'warning';
            case 'DELETE': return 'error';
            default: return 'default';
        }
    };

    const renderFieldTable = (fields: ApiField[], title: string) => {
        const requiredFields = fields.filter(f => f.required);
        const optionalFields = fields.filter(f => !f.required);
        const fieldsToShow = title.includes('Required') ? requiredFields : optionalFields;

        if (fieldsToShow.length === 0) return null;

        return (
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        {title} ({fieldsToShow.length})
                    </Typography>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell><strong>Field Name</strong></TableCell>
                                    <TableCell><strong>API Field</strong></TableCell>
                                    <TableCell><strong>Type</strong></TableCell>
                                    <TableCell><strong>Description</strong></TableCell>
                                    <TableCell><strong>Example</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {fieldsToShow.map((field) => (
                                    <TableRow key={field.apiFieldName}>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: field.required ? 'bold' : 'normal' }}>
                                                {field.name}
                                                {field.required && <Chip label="Required" size="small" color="error" sx={{ ml: 1 }} />}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" component="code" sx={{ backgroundColor: 'grey.100', px: 1, py: 0.5, borderRadius: 1 }}>
                                                {field.apiFieldName}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {field.type}
                                                {field.units && ` (${field.units})`}
                                                {field.maxLength && ` (max ${field.maxLength})`}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: '300px' }}>
                                            <Typography variant="body2">
                                                {field.description}
                                                {field.fieldType === 'fixed' && (
                                                    <Box sx={{ mt: 1 }}>
                                                        <Typography variant="caption" display="block" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                                            {field.options && field.options.length > 0 
                                                                ? `Available Options: ${field.options.join(', ')}`
                                                                : 'Available Options: Currently not available'
                                                            }
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {field.example && (
                                                <Typography variant="body2" component="code" sx={{ backgroundColor: 'grey.100', px: 1, py: 0.5, borderRadius: 1 }}>
                                                    {field.example}
                                                </Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>
        );
    };

    if (loading) {
        return (
            <Container maxWidth="xl" sx={{ py: 4 }}>
                <Typography>Loading API documentation...</Typography>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="xl" sx={{ py: 4 }}>
                <Alert severity="error">Error loading API documentation: {error}</Alert>
            </Container>
        );
    }

    return (
        <Box sx={{ flexGrow: 1 }}>
            <AppBar position="static" elevation={0} sx={{ borderBottom: 1, borderColor: 'divider', backgroundColor: 'background.paper' }}>
                <Toolbar>
                    <IconButton
                        edge="start"
                        color="primary"
                        onClick={() => navigate('/')}
                        sx={{ mr: 2 }}
                    >
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: 'text.primary' }}>
                        Doyle Caribbean Customer API Documentation
                    </Typography>
                    <Chip 
                        icon={<SecurityIcon />} 
                        label="Authenticated Access Required" 
                        color="secondary" 
                        variant="outlined" 
                    />
                </Toolbar>
            </AppBar>

            <Container maxWidth="xl" sx={{ py: 4 }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                    <Tabs value={activeTab} onChange={handleTabChange}>
                        <Tab icon={<DescriptionIcon />} label="Overview" iconPosition="start" />
                        <Tab icon={<CodeIcon />} label="API Fields" iconPosition="start" />
                        <Tab label="Endpoints" />
                        <Tab icon={<SecurityIcon />} label="Authentication" iconPosition="start" />
                    </Tabs>
                </Box>

                <TabPanel value={activeTab} index={0}>
                    <Stack spacing={3}>
                        <Paper sx={{ p: 4 }}>
                            <Typography variant="h4" gutterBottom>
                                Doyle Caribbean Customer API
                            </Typography>
                            <Typography variant="subtitle1" color="text.secondary" paragraph>
                                Professional API for system-to-system sail quote integration
                            </Typography>
                            <Typography paragraph>
                                This API enables technically advanced customers to submit sail quotes directly into our system 
                                and integrate with their own systems. The API supports the complete sail ordering workflow from 
                                initial quote submission through order confirmation and status tracking.
                            </Typography>
                        </Paper>

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={4}>
                                <Card>
                                    <CardContent sx={{ textAlign: 'center' }}>
                                        <SpeedIcon color="primary" sx={{ fontSize: 40, mb: 2 }} />
                                        <Typography variant="h6" gutterBottom>
                                            Rate Limited
                                        </Typography>
                                        <Typography variant="body2">
                                            50 requests per second per API key
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Card>
                                    <CardContent sx={{ textAlign: 'center' }}>
                                        <SecurityIcon color="primary" sx={{ fontSize: 40, mb: 2 }} />
                                        <Typography variant="h6" gutterBottom>
                                            Secure Access
                                        </Typography>
                                        <Typography variant="body2">
                                            API key authentication required
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Card>
                                    <CardContent sx={{ textAlign: 'center' }}>
                                        <CodeIcon color="primary" sx={{ fontSize: 40, mb: 2 }} />
                                        <Typography variant="h6" gutterBottom>
                                            REST API
                                        </Typography>
                                        <Typography variant="body2">
                                            JSON-based REST endpoints
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>

                        <Alert severity="info">
                            <Typography variant="subtitle2" gutterBottom>
                                Getting Started
                            </Typography>
                            <Typography variant="body2">
                                1. Contact Doyle Caribbean to obtain your API credentials<br />
                                2. Use the Authentication tab to learn about API key usage<br />
                                3. Review the API Fields tab for complete field specifications<br />
                                4. Test your integration using the provided endpoints
                            </Typography>
                        </Alert>
                    </Stack>
                </TabPanel>

                <TabPanel value={activeTab} index={1}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                        <Typography variant="h5">
                            API Field Specifications
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                            {isFromAdminConsole ? (
                                <Chip 
                                    label="Live from Admin Console" 
                                    color="success" 
                                    size="small"
                                    icon={<RefreshIcon />}
                                />
                            ) : (
                                <Chip 
                                    label="Static Definitions" 
                                    color="warning" 
                                    size="small"
                                />
                            )}
                            <Typography variant="body2" color="text.secondary">
                                {apiFields.length} fields
                            </Typography>
                        </Stack>
                    </Stack>
                    <Typography paragraph>
                        These fields define the complete sail specification that can be submitted via the API. 
                        {isFromAdminConsole ? (
                            'Field definitions are loaded live from the admin console and reflect the latest approved options.'
                        ) : (
                            'Field definitions are using fallback data. Contact administrator if live data is needed.'
                        )}
                    </Typography>

                    {renderFieldTable(apiFields, 'Required Fields')}
                    {renderFieldTable(apiFields, 'Optional Fields')}
                </TabPanel>

                <TabPanel value={activeTab} index={2}>
                    <Typography variant="h5" gutterBottom>
                        API Endpoints
                    </Typography>
                    
                    {apiEndpoints.map((endpoint, index) => (
                        <Accordion key={index} sx={{ mb: 2 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item>
                                        <Chip 
                                            label={endpoint.method} 
                                            color={getMethodColor(endpoint.method)}
                                            size="small"
                                        />
                                    </Grid>
                                    <Grid item xs>
                                        <Typography variant="subtitle1">{endpoint.summary}</Typography>
                                    </Grid>
                                    <Grid item>
                                        <Typography variant="body2" color="text.secondary" component="code">
                                            {endpoint.path}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Stack spacing={3}>
                                    <Typography variant="body1">
                                        {endpoint.description}
                                    </Typography>

                                    {endpoint.parameters && (
                                        <Box>
                                            <Typography variant="h6" gutterBottom>
                                                Parameters
                                            </Typography>
                                            <TableContainer component={Paper} variant="outlined">
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>Parameter</TableCell>
                                                            <TableCell>Type</TableCell>
                                                            <TableCell>Required</TableCell>
                                                            <TableCell>Description</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {endpoint.parameters.map((param, i) => (
                                                            <TableRow key={i}>
                                                                <TableCell component="code">{param.apiFieldName}</TableCell>
                                                                <TableCell>{param.type}</TableCell>
                                                                <TableCell>
                                                                    {param.required ? (
                                                                        <Chip label="Yes" size="small" color="error" />
                                                                    ) : (
                                                                        <Chip label="No" size="small" />
                                                                    )}
                                                                </TableCell>
                                                                <TableCell>{param.description}</TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Box>
                                    )}

                                    <Box>
                                        <Typography variant="h6" gutterBottom>
                                            Responses
                                        </Typography>
                                        {Object.entries(endpoint.responses).map(([status, response]) => (
                                            <Paper key={status} sx={{ p: 2, mb: 2 }} variant="outlined">
                                                <Typography variant="subtitle2" gutterBottom>
                                                    Status {status}: {response.description}
                                                </Typography>
                                                {response.example && (
                                                    <Box sx={{ position: 'relative' }}>
                                                        <pre style={{ 
                                                            backgroundColor: '#f5f5f5', 
                                                            padding: '16px', 
                                                            borderRadius: '4px',
                                                            overflow: 'auto',
                                                            fontSize: '0.875rem'
                                                        }}>
                                                            {JSON.stringify(response.example, null, 2)}
                                                        </pre>
                                                        <Tooltip title="Copy Response Example">
                                                            <IconButton
                                                                size="small"
                                                                sx={{ position: 'absolute', top: 8, right: 8 }}
                                                                onClick={() => copyToClipboard(JSON.stringify(response.example, null, 2))}
                                                            >
                                                                <ContentCopyIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                )}
                                            </Paper>
                                        ))}
                                    </Box>
                                </Stack>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </TabPanel>

                <TabPanel value={activeTab} index={3}>
                    <Typography variant="h5" gutterBottom>
                        Authentication
                    </Typography>
                    
                    <Stack spacing={3}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                API Key Authentication
                            </Typography>
                            <Typography paragraph>
                                All API requests must include your API key in the request headers:
                            </Typography>
                            <Paper sx={{ p: 2, bgcolor: 'grey.50', position: 'relative' }}>
                                <Typography variant="body2" component="code">
                                    X-API-Key: your-api-key-here
                                </Typography>
                                <Tooltip title="Copy Header Example">
                                    <IconButton
                                        size="small"
                                        sx={{ position: 'absolute', top: 8, right: 8 }}
                                        onClick={() => copyToClipboard('X-API-Key: your-api-key-here')}
                                    >
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Paper>
                        </Paper>

                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Rate Limiting
                            </Typography>
                            <Typography paragraph>
                                API requests are limited to <strong>50 requests per second per API key</strong>. 
                                If you exceed this limit, you'll receive a 429 status code.
                            </Typography>
                            <Alert severity="warning">
                                Distribute your requests evenly to avoid hitting rate limits. 
                                Consider implementing exponential backoff for retries.
                            </Alert>
                        </Paper>

                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Obtaining API Credentials
                            </Typography>
                            <Typography paragraph>
                                To obtain API credentials for testing or production use:
                            </Typography>
                            <Typography component="ol" sx={{ pl: 3 }}>
                                <li>Contact Doyle Caribbean technical support</li>
                                <li>Provide your business information and intended use case</li>
                                <li>Specify whether you need test or production credentials</li>
                                <li>Receive your unique API key and base URL</li>
                            </Typography>
                            <Alert severity="info">
                                Keep your API key secure and never expose it in client-side code or public repositories.
                            </Alert>
                        </Paper>
                    </Stack>
                </TabPanel>
            </Container>

            {/* Floating refresh button */}
            <Fab
                color="primary"
                size="medium"
                sx={{ position: 'fixed', bottom: 16, right: 16 }}
                onClick={refreshDocumentation}
                disabled={loading}
            >
                <RefreshIcon />
            </Fab>
        </Box>
    );
};